import type {
  OperationLog,
  OperationLogEntryType,
} from '@ops/entities/operation-log';
import { UTCDate } from '@date-fns/utc';

// Human verb per entry type (design: "Assigned driver · <service>"). Unmapped
// types fall back to a humanised entry-type label.
const VERB: Partial<Record<OperationLogEntryType, string>> = {
  status_change: 'Set status',
  tour_operator_update: 'Assigned driver / guide',
  supplier_update: 'Updated supplier',
  alert: 'Flagged',
  whatsapp_notification: 'Sent message',
  whatsapp_confirmation: 'Confirmed message',
  date_update: 'Rescheduled',
  added_removed: 'Added / removed item',
  modified: 'Edited item',
  notes_update: 'Updated notes',
  booking_status_change: 'Changed booking status',
  price_update: 'Updated price',
  location_update: 'Updated location',
  visibility_change: 'Changed visibility',
};

const humanize = (entryType: string): string => {
  const words = entryType.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// Item title jsonb → display string (i18n array / `{en}` / plain string).
const resolveTitle = (title: unknown): string => {
  if (typeof title === 'string') return title;
  if (Array.isArray(title)) {
    const pick =
      (title as Array<{ languageCode?: string; text?: string }>).find(
        (t) => t.languageCode === 'en' && t.text
      ) ?? (title as Array<{ text?: string }>).find((t) => t.text);
    return pick?.text ?? '';
  }
  if (title && typeof title === 'object' && 'en' in title) {
    return String((title as Record<string, unknown>).en ?? '');
  }
  return '';
};

export const describeLogEntry = (entry: OperationLog): string => {
  const verb = VERB[entry.entryType] ?? humanize(entry.entryType);
  const title = resolveTitle(entry.itemTitle);
  return title ? `${verb} · ${title}` : verb;
};

// `operation_log.created_on` is a `withTimezone` timestamp — Postgres emits it
// as e.g. "2026-07-03 12:34:56.789123+00" (space separator, µs, 2-digit offset).
// Normalise to a valid ISO string (T separator, ms precision, `+00:00` offset)
// so UTCDate parses it — the naked-string `toUtcDate` helper mis-handles the
// 2-digit offset.
const parseTs = (raw: string): number => {
  const iso = (raw.includes('T') ? raw : raw.replace(' ', 'T'))
    .replace(/(\.\d{3})\d+/, '$1')
    .replace(/([+-]\d{2})$/, '$1:00');
  return new UTCDate(iso).getTime();
};

// Compact relative time ("4m ago", "1h ago", "2d ago") vs a fixed reference
// millisecond timestamp so a panel of entries reads consistently.
export const timeAgo = (createdOn: string, nowMs: number): string => {
  const then = parseTs(createdOn);
  if (!Number.isFinite(then)) return '';
  const minutes = Math.max(0, Math.floor((nowMs - then) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};
